import React, { useEffect, useRef, useState } from 'react';
import { CheckCircle2 } from 'lucide-react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { slides } from '../mock'; // only used for the number of text slides / dots now
import { useLang } from '../i18n/LanguageContext';
import { Link } from 'react-router-dom';

export default function HeroSlider() {
  const [idx, setIdx] = useState(0);
  const videoRef = useRef(null);
  const { t } = useLang();
  const { scrollY } = useScroll();
  const bgY = useTransform(scrollY, [0, 700], [0, 160]);

  useEffect(() => {
    const t2 = setInterval(() => setIdx((i) => (i + 1) % slides.length), 6000);
    return () => clearInterval(t2);
  }, []);

  // React does not always render the `muted` attribute, and browsers only autoplay muted videos - so set it explicitly.
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = true;
    const p = v.play();
    if (p && p.catch) p.catch(() => { /* autoplay blocked (e.g. battery saver) - the poster image stays visible */ });
  }, []);

  const slideTitle = t(`hero.slide${idx + 1}.t`);
  const slideSub = t(`hero.slide${idx + 1}.s`);
  const words = slideTitle.split(' ');

  return (
    <section className="relative h-screen w-full overflow-hidden" data-testid="hero-slider">
      {/* Background VIDEO (replaces the sliding images) - files are in public/videos/ */}
      <motion.div style={{ y: bgY }} className="absolute inset-0">
        <video
          ref={videoRef}
          className="absolute inset-0 w-full h-full object-cover"
          src={`${process.env.PUBLIC_URL}/videos/hero-video.mp4`}
          poster={`${process.env.PUBLIC_URL}/videos/hero-poster.jpg`}
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          aria-hidden="true"
          data-testid="hero-video"
        />
        {/* light dark overlay so the white text stays readable (video is already fairly dark) */}
        <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(15,16,32,0.2) 0%, rgba(15,16,32,0.4) 100%)' }} />
      </motion.div>

      {/* Text block: aligned to the left edge (no max-width / auto margins, so it is not centred) */}
      <div className="relative z-10 h-full w-full px-6 lg:px-16 flex flex-col justify-center items-start text-left text-white">
        <h1 key={`t-${idx}`} className="text-4xl md:text-6xl lg:text-7xl font-bold leading-[1.08] max-w-4xl" data-testid="hero-title">
          {words.map((w, i) => (
            <span key={i} className="inline-block overflow-hidden align-bottom pb-2 -mb-2">
              <motion.span
                className="inline-block"
                initial={{ y: '115%' }}
                animate={{ y: 0 }}
                transition={{ duration: 0.8, delay: 0.2 + i * 0.07, ease: [0.22, 1, 0.36, 1] }}
              >
                {w}{i < words.length - 1 ? ' ' : ''}
              </motion.span>
            </span>
          ))}
        </h1>
        <motion.p
          key={`s-${idx}`}
          className="mt-4 text-lg md:text-xl text-white/85"
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.55 }}
          data-testid="hero-subtitle"
        >
          {slideSub}
        </motion.p>
        <motion.div
          key={`c-${idx}`}
          className="mt-8 flex flex-wrap gap-3"
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.75 }}
        >
          <Link to="/quote" data-testid="hero-get-quote-btn" className="btn-brand inline-flex items-center gap-2 uppercase tracking-widest text-xs">
            <CheckCircle2 size={16} /> {t('cta.getQuote')}
          </Link>
          <Link to="/track" data-testid="hero-track-btn" className="btn-brand inline-flex items-center gap-2 uppercase tracking-widest text-xs">
            {t('cta.trackShipment')}
          </Link>
        </motion.div>
      </div>

      {/* Dots */}
      <div className="absolute bottom-10 inset-x-0 z-10 flex items-center justify-center gap-2">
        {slides.map((_, i) => (
          <button key={i} aria-label={`Slide ${i + 1}`} data-testid={`hero-dot-${i}`} className={`dot ${i === idx ? 'active' : ''}`} onClick={() => setIdx(i)} />
        ))}
      </div>

      {/* Scroll down indicator */}
      {/* Not an <a href="#about">: with HashRouter that would change the route instead of scrolling */}
      <button
        type="button"
        data-testid="hero-scroll-indicator"
        onClick={() => document.getElementById('about')?.scrollIntoView({ behavior: 'smooth' })}
        className="absolute bottom-20 right-8 z-10 hidden md:flex flex-col items-center gap-2 text-white/70 hover:text-white transition-colors"
      >
        <span className="text-[10px] uppercase tracking-widest">{t('scroll')}</span>
        <span className="h-10 w-[1px] bg-white/60 animate-pulse" />
      </button>
    </section>
  );
}
